/**
 * Shapes returned by `GET /dice/notifications/sends`, plus the pure tab, label
 * and search helpers behind the sends dashboard. Kept free of React and axios
 * so it can be unit tested directly.
 */

import { channelLabel } from './notificationDeliveryModel';

export enum NotificationSendStatus {
  CREATED = 'created',
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  PROCESSING = 'processing',
  CANCELLED = 'cancelled',
  SENT = 'sent',
  FAILED = 'failed',
  PARTIAL = 'partial',
}

export enum NotificationSource {
  MANUAL = 'manual',
  ASSIGNMENT = 'assignment',
  REMINDER = 'reminder',
  ACTIVATION = 'activation',
  AUTOMATION = 'automation',
  SCENARIO = 'scenario',
  FOLLOW_UP = 'followUp',
}

export type NotificationDeliveryStats = {
  recipients: number;
  reached: number;
  delivered: number;
  responded: number;
  failed: number;
  maxEscalationStep: number;
};

export type NotificationSendDto = {
  id: string;
  subject: string | null;
  priority: string;
  status: NotificationSendStatus;
  source: NotificationSource;
  incidentId: string | null;
  createdAt: string | null;
  scheduledDate: string | null;
  sentAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  channels: string[];
  escalationStepCount: number;
  stats: NotificationDeliveryStats;
};

export enum SendsTab {
  SENT = 'sent',
  SCHEDULED = 'scheduled',
  DRAFTS = 'drafts',
  FAILED = 'failed',
}

export const SENDS_TAB_CHOICES: { label: string; value: SendsTab }[] = [
  { label: 'Sent', value: SendsTab.SENT },
  { label: 'Scheduled', value: SendsTab.SCHEDULED },
  { label: 'Drafts', value: SendsTab.DRAFTS },
  { label: 'Failed', value: SendsTab.FAILED },
];

/**
 * Mirrors `tabOf` in coms.
 *
 * `processing` belongs under Sent: the operator's question is whether a message
 * has left, and one mid-fan-out has. `cancelled` also lands there, because the
 * record of calling something off is part of the send history rather than a
 * pending item.
 */
export function tabOf(status: NotificationSendStatus): SendsTab {
  switch (status) {
    case NotificationSendStatus.DRAFT:
      return SendsTab.DRAFTS;
    case NotificationSendStatus.SCHEDULED:
      return SendsTab.SCHEDULED;
    case NotificationSendStatus.FAILED:
      return SendsTab.FAILED;
    default:
      return SendsTab.SENT;
  }
}

export const SOURCE_LABEL: Record<NotificationSource, string> = {
  [NotificationSource.MANUAL]: 'Manual',
  [NotificationSource.ASSIGNMENT]: 'Assignment',
  [NotificationSource.REMINDER]: 'Reminder',
  [NotificationSource.ACTIVATION]: 'Activation',
  [NotificationSource.AUTOMATION]: 'Automation',
  [NotificationSource.SCENARIO]: 'Scenario',
  [NotificationSource.FOLLOW_UP]: 'Follow-up',
};

export const SEND_STATUS_LABEL: Record<NotificationSendStatus, string> = {
  [NotificationSendStatus.CREATED]: 'Queued',
  [NotificationSendStatus.DRAFT]: 'Draft',
  [NotificationSendStatus.SCHEDULED]: 'Scheduled',
  [NotificationSendStatus.PROCESSING]: 'Sending',
  [NotificationSendStatus.CANCELLED]: 'Cancelled',
  [NotificationSendStatus.SENT]: 'Sent',
  [NotificationSendStatus.FAILED]: 'Failed',
  [NotificationSendStatus.PARTIAL]: 'Partly sent',
};

export function sendStatusTone(
  status: NotificationSendStatus
): 'neutral' | 'good' | 'warn' | 'bad' {
  switch (status) {
    case NotificationSendStatus.SENT:
      return 'good';
    case NotificationSendStatus.PARTIAL:
      return 'warn';
    case NotificationSendStatus.FAILED:
      return 'bad';
    default:
      return 'neutral';
  }
}

/** A send with no subject still needs something to click on. */
export function sendTitle(send: NotificationSendDto): string {
  const subject = send.subject?.trim();
  return subject || '(No subject)';
}

export function channelsLabel(channels: string[]): string {
  if (channels.length === 0) return '—';
  return channels.map(channelLabel).join(', ');
}

/**
 * How far a cascade got, as "2 of 3".
 *
 * Empty for a send with no plan, so the column stays quiet for the ordinary
 * case rather than showing "0 of 0" on every row.
 */
export function escalationLabel(send: NotificationSendDto): string {
  if (send.escalationStepCount === 0) return '';
  return `${send.stats.maxEscalationStep} of ${send.escalationStepCount}`;
}

/**
 * Recipients still owed a response, which is the number that decides whether a
 * sender needs to do something.
 */
export function awaitingResponse(send: NotificationSendDto): number {
  return Math.max(send.stats.reached - send.stats.responded, 0);
}

/**
 * Whether calling this send off is still possible.
 *
 * Mirrors the server's own list; showing a Cancel button that would be refused
 * is worse than not showing one.
 */
export function isCancellable(send: NotificationSendDto): boolean {
  return (
    send.status === NotificationSendStatus.SCHEDULED ||
    send.status === NotificationSendStatus.CREATED ||
    send.status === NotificationSendStatus.DRAFT
  );
}

/**
 * Search covers the title, sender, source and channels, so an operator chasing
 * a complaint can paste in whatever they were told.
 */
export function filterSends(
  sends: NotificationSendDto[],
  tab: SendsTab,
  search: string
): NotificationSendDto[] {
  const term = search.trim().toLowerCase();

  return sends.filter((send) => {
    if (tabOf(send.status) !== tab) return false;
    if (!term) return true;

    return [
      sendTitle(send),
      send.createdByName ?? '',
      SOURCE_LABEL[send.source] ?? send.source,
      SEND_STATUS_LABEL[send.status] ?? send.status,
      send.priority,
      channelsLabel(send.channels),
    ]
      .join(' ')
      .toLowerCase()
      .includes(term);
  });
}

/** How many rows each tab would show, for the tab labels. */
export function tabCounts(
  sends: NotificationSendDto[]
): Record<SendsTab, number> {
  const counts: Record<SendsTab, number> = {
    [SendsTab.SENT]: 0,
    [SendsTab.SCHEDULED]: 0,
    [SendsTab.DRAFTS]: 0,
    [SendsTab.FAILED]: 0,
  };

  sends.forEach((send) => {
    counts[tabOf(send.status)] += 1;
  });

  return counts;
}

/**
 * When a send happened, or is going to.
 *
 * A scheduled send has no `sentAt` yet, and showing its creation time would
 * misrepresent it as already gone.
 */
export function sendTimestamp(send: NotificationSendDto): string | null {
  return send.sentAt ?? send.scheduledDate ?? send.createdAt;
}
